import type { Session } from '@supabase/supabase-js';

export interface BufferedAuthEvent {
  readonly event: string;
  readonly session: Session | null;
}

export interface AuthEventBuffer {
  readonly push: (event: string, session: Session | null) => Promise<void>;
  readonly release: () => Promise<void>;
  readonly snapshot: () => readonly BufferedAuthEvent[];
}

function ignoreAuthEventFailure(): undefined {
  return undefined;
}

/** Preserve auth event order while cold-launch state is classified and hydrated. */
export function createAuthEventBuffer(
  applyEvent: (event: string, session: Session | null) => Promise<void>,
): AuthEventBuffer {
  let released = false;
  const pending: BufferedAuthEvent[] = [];
  let chain = Promise.resolve();

  const enqueue = async (event: string, session: Session | null): Promise<void> => {
    const run = chain.then(async () => {
      await applyEvent(event, session);
    });
    chain = run.catch(ignoreAuthEventFailure);
    await run;
  };

  return {
    push: async (event, session) => {
      if (!released) {
        pending.push({ event, session });
        return;
      }
      await enqueue(event, session);
    },
    release: async () => {
      released = true;
      const runs = pending
        .splice(0)
        .map(async (buffered) => await enqueue(buffered.event, buffered.session));
      const results = await Promise.allSettled(runs);
      const rejected = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      if (rejected) {
        throw rejected.reason instanceof Error
          ? rejected.reason
          : new Error('Auth event handler failed');
      }
    },
    snapshot: () => [...pending],
  };
}
